// Settings › Automation › Follow-up tasks (workflow B, approved fixture
// docs/plans/workflow-improvements/preview-v3 `SettingsFollowups`). Each
// trigger holds 1–6 task rules; every change autosaves the whole trigger with
// PUT /api/admin/followup-tasks/:key, the hub's pattern — switches on click,
// text and number on blur or Enter. The UI updates optimistically and reverts
// to the last saved server state when a save fails. Saves are serialized per
// trigger: edits made while a PUT is in flight coalesce into one follow-up PUT
// of the latest state. Phone rules (and phone "+ Add task") open a half sheet
// whose edits stay a local draft until Save.

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import { Modal, SkeletonRows } from './primitives';

type TriggerKey = 'project_delivered' | 'invoice_sent' | 'invoice_paid' | 'lead_proposal_sent';

interface Rule {
  id: string;
  title: string;
  offset_days: number;
  enabled: boolean;
}

interface Trigger {
  key: TriggerKey;
  title: string;
  eyebrow: string;
  anchor: string;
  desc: string;
  enabled: boolean;
  rules: Rule[];
}

/** A server validation error pinned to one rule's field (or the trigger). */
interface FieldError {
  ruleId: string | null;
  field: 'title' | 'offset_days' | null;
  message: string;
}

const TITLE_MAX = 120;
const OFFSET_MAX = 365;
const RULES_MAX = 6;
const KEEP_ONE = 'Keep at least one task';
const TITLE_ERROR = `Title needs 1–${TITLE_MAX} characters`;
const OFFSET_ERROR = `Enter 0–${OFFSET_MAX} days`;

function newRuleId(): string {
  return `r_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function validTitle(title: string): boolean {
  const t = title.trim();
  return t.length >= 1 && t.length <= TITLE_MAX;
}

function validOffset(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n <= OFFSET_MAX;
}

/** A typed day field: blank is invalid, not 0 (`Number('')` is 0). */
function parseOffset(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return validOffset(n) ? n : null;
}

function dueLabel(offset: number, trigger: Trigger): string {
  if (offset === 0) {
    return trigger.anchor === 'after payment'
      ? 'Due on the payment day'
      : `Due the same day (${trigger.anchor.replace(/^after /, '')})`;
  }
  return `Due ${offset} day${offset === 1 ? '' : 's'} ${trigger.anchor}`;
}

/** Maps the server's `rules.<i>.<field>` onto the rule that was sent at i. */
function fieldErrorFrom(err: unknown, sent: Rule[]): FieldError | null {
  if (!(err instanceof ApiError)) return null;
  const field = typeof err.body.field === 'string' ? err.body.field : null;
  if (!field) return null;
  const m = /^rules\.(\d+)\.(title|offset_days)$/.exec(field);
  if (m) {
    const rule = sent[Number(m[1])];
    return { ruleId: rule?.id ?? null, field: m[2] as 'title' | 'offset_days', message: err.message };
  }
  return { ruleId: null, field: null, message: err.message };
}

export function FollowupTasksCard({
  active,
  mobile,
  onSaved,
  onError,
}: {
  /** Loads once, the first time the Automation tab is shown. */
  active: boolean;
  mobile: boolean;
  onSaved: (message: string) => void;
  onError: (message: string | null) => void;
}) {
  const [triggers, setTriggers] = useState<Trigger[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<TriggerKey, FieldError>>>({});
  const [editing, setEditing] = useState<{
    key: TriggerKey;
    draft: Rule;
    draftOffset: string;
    error: string | null;
    /** A phone "+ Add task" draft — not on the server until Save. */
    isNew: boolean;
  } | null>(null);
  /** Last server-confirmed copy of each trigger — the rollback baseline. */
  const saved = useRef<Partial<Record<TriggerKey, Trigger>>>({});
  /** Newest local copy of each trigger (what the screen shows). */
  const local = useRef<Partial<Record<TriggerKey, Trigger>>>({});
  const inFlight = useRef<Partial<Record<TriggerKey, boolean>>>({});
  /** Local changes made while a PUT was in flight, with their toast message. */
  const dirty = useRef<Partial<Record<TriggerKey, string>>>({});
  /** True while the Settings error banner shows an error this card raised. */
  const ownsError = useRef(false);
  const requested = useRef(false);

  useEffect(() => {
    if (!active || requested.current) return;
    requested.current = true;
    api<{ triggers: Trigger[] }>('/api/admin/followup-tasks')
      .then((res) => {
        for (const t of res.triggers) {
          saved.current[t.key] = t;
          local.current[t.key] = t;
        }
        setTriggers(res.triggers);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Could not load follow-up tasks.'));
  }, [active]);

  const replace = (next: Trigger) =>
    setTriggers((ts) => ts?.map((t) => (t.key === next.key ? next : t)) ?? ts);

  const clearOwnError = () => {
    if (!ownsError.current) return;
    ownsError.current = false;
    onError(null);
  };

  /** PUTs the newest local copy of `key`. One PUT per trigger at a time. */
  const send = (key: TriggerKey, message: string) => {
    const body = local.current[key];
    if (!body) return;
    inFlight.current[key] = true;
    api<Trigger>(`/api/admin/followup-tasks/${key}`, {
      method: 'PUT',
      json: { enabled: body.enabled, rules: body.rules },
    })
      .then((res) => {
        inFlight.current[key] = false;
        // Saves are serialized, so this response is the server's current
        // state — the right baseline even when a follow-up PUT is queued.
        saved.current[key] = res;
        const queued = dirty.current[key];
        if (queued !== undefined) {
          delete dirty.current[key];
          send(key, queued);
          return;
        }
        local.current[key] = res;
        replace(res);
        clearOwnError();
        onSaved(message);
      })
      .catch((err) => {
        inFlight.current[key] = false;
        delete dirty.current[key];
        const last = saved.current[key];
        if (last) {
          local.current[key] = last;
          replace(last);
        }
        ownsError.current = true;
        onError(err instanceof Error ? err.message : 'Could not save follow-up tasks.');
        const fe = fieldErrorFrom(err, body.rules);
        if (fe) setFieldErrors((cur) => ({ ...cur, [key]: fe }));
      });
  };

  /** Optimistically shows `next` and saves it — now, or after the in-flight
      PUT for this trigger settles (coalesced with any other queued edits). */
  const save = (next: Trigger, message = 'Saved') => {
    const key = next.key;
    local.current[key] = next;
    replace(next);
    setFieldErrors((fe) => ({ ...fe, [key]: undefined }));
    if (inFlight.current[key]) dirty.current[key] = message;
    else send(key, message);
  };

  const patchRule = (t: Trigger, ruleId: string, patch: Partial<Rule>) =>
    save({ ...t, rules: t.rules.map((r) => (r.id === ruleId ? { ...r, ...patch } : r)) });

  const blankRule = (): Rule => ({ id: newRuleId(), title: 'New task', offset_days: 7, enabled: true });

  /** Desktop: appends a "New task" rule and saves at once. */
  const addRule = (t: Trigger) => {
    if (t.rules.length >= RULES_MAX) return;
    save({ ...t, rules: [...t.rules, blankRule()] });
  };

  const removeRule = (t: Trigger, ruleId: string) => {
    if (t.rules.length <= 1) return;
    save({ ...t, rules: t.rules.filter((r) => r.id !== ruleId) }, 'Task removed');
  };

  const onCount = (triggers ?? []).reduce(
    (n, t) => n + (t.enabled ? t.rules.filter((r) => r.enabled).length : 0),
    0,
  );

  const Switch = ({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) => (
    <button
      type="button"
      className={`${mobile ? 'toggle' : 'atm-switch'}${on ? ' on' : ''}`}
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
    />
  );

  const editTrigger = editing ? triggers?.find((t) => t.key === editing.key) ?? null : null;
  /** The sheet's day field: null when blank or out of range. */
  const draftOffset = editing ? parseOffset(editing.draftOffset) : null;

  const saveSheet = () => {
    if (!editing || !editTrigger) return;
    const title = editing.draft.title.trim();
    if (!validTitle(title)) {
      setEditing({ ...editing, error: TITLE_ERROR });
      return;
    }
    if (draftOffset === null) {
      setEditing({ ...editing, error: OFFSET_ERROR });
      return;
    }
    const next: Rule = { ...editing.draft, title, offset_days: draftOffset };
    setEditing(null);
    if (editing.isNew) {
      if (editTrigger.rules.length < RULES_MAX) save({ ...editTrigger, rules: [...editTrigger.rules, next] });
      return;
    }
    const current = editTrigger.rules.find((r) => r.id === next.id);
    if (
      current &&
      current.title === next.title &&
      current.offset_days === next.offset_days &&
      current.enabled === next.enabled
    ) {
      return;
    }
    patchRule(editTrigger, next.id, next);
  };

  const openSheet = (key: TriggerKey, rule: Rule, isNew = false) =>
    setEditing({ key, draft: { ...rule }, draftOffset: String(rule.offset_days), error: null, isNew });

  /** − / + step from the typed value; a blank field steps from 0. */
  const stepOffset = (delta: number) => {
    if (!editing) return;
    const typed = Number(editing.draftOffset);
    const base = Number.isFinite(typed) ? Math.round(typed) : 0;
    const n = Math.min(OFFSET_MAX, Math.max(0, base + delta));
    setEditing({ ...editing, draftOffset: String(n), error: null });
  };

  return (
    <div className="settings-card ft-card">
      <div className="sc-head">
        <span className="sc-title">Follow-up tasks</span>
        {triggers && (
          <span className={`sc-meta${onCount ? ' on' : ''}`}>
            {onCount} task{onCount === 1 ? '' : 's'} on
          </span>
        )}
      </div>
      <div className="sc-desc">
        When one of these events happens, the OS adds the tasks below to the project (or client) with the due date
        shown. Changes apply to future events only — tasks already created are never rewritten, and each event
        creates its tasks once.
      </div>

      {!triggers && !loadError && <SkeletonRows rows={4} />}
      {loadError && <div className="error-text">{loadError}</div>}

      {triggers?.map((t) => {
        const fe = fieldErrors[t.key];
        const onlyOne = t.rules.length <= 1;
        return (
          <div key={t.key} className={`ft-trigger${t.enabled ? '' : ' off'}`}>
            <div className="ft-trigger-head">
              <div className="ft-trigger-title">
                <span>{t.title}</span>
                <span className="ft-eyebrow">{t.eyebrow}</span>
              </div>
              <Switch
                on={t.enabled}
                label={`Turn ${t.title} ${t.enabled ? 'off' : 'on'}`}
                onClick={() => save({ ...t, enabled: !t.enabled })}
              />
            </div>
            {!mobile && <p className="ft-desc">{t.desc}</p>}

            {mobile ? (
              <>
                {t.rules.map((r) => (
                  <button key={r.id} type="button" className="settings-link-row" onClick={() => openSheet(t.key, r)}>
                    <span>
                      <span className="ft-m-title">{r.title}</span>
                      <span className="ft-m-meta">{r.enabled ? dueLabel(r.offset_days, t) : 'Off'}</span>
                    </span>
                    <span className="settings-row-action">Edit ›</span>
                  </button>
                ))}
                {fe && <span className="error-text ft-error" role="alert">{fe.message}</span>}
                {t.rules.length < RULES_MAX && (
                  <button
                    type="button"
                    className="settings-link-row ft-m-add"
                    onClick={() => openSheet(t.key, blankRule(), true)}
                  >
                    <span>+ Add task</span>
                  </button>
                )}
              </>
            ) : (
              <>
                {t.rules.map((r) => (
                  <RuleRow
                    key={r.id}
                    rule={r}
                    trigger={t}
                    switchEl={
                      <Switch
                        on={r.enabled}
                        label={`Turn task ${r.title} ${r.enabled ? 'off' : 'on'}`}
                        onClick={() => patchRule(t, r.id, { enabled: !r.enabled })}
                      />
                    }
                    error={fe && fe.ruleId === r.id ? fe : null}
                    onlyOne={onlyOne}
                    onSave={(patch) => patchRule(t, r.id, patch)}
                    onRemove={() => removeRule(t, r.id)}
                  />
                ))}
                {fe && !t.rules.some((r) => r.id === fe.ruleId) && (
                  <span className="error-text ft-error" role="alert">{fe.message}</span>
                )}
                {t.rules.length < RULES_MAX && (
                  <button type="button" className="group-link ft-add" onClick={() => addRule(t)}>
                    + Add task
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}

      {triggers && (
        <div className="ft-tokens">
          Titles can include {'{project}'} · {'{client}'} · {'{invoice}'} · {'{lead}'} — filled in when the task is
          created.
        </div>
      )}

      {editing && editTrigger && (
        <Modal
          title={editing.isNew ? 'Add task' : 'Edit task'}
          context={editTrigger.title}
          onClose={() => setEditing(null)}
          mobile="half"
          hint={draftOffset !== null ? dueLabel(draftOffset, editTrigger) : undefined}
          foot={
            <>
              <button
                className="btn danger"
                disabled={!editing.isNew && editTrigger.rules.length <= 1}
                title={!editing.isNew && editTrigger.rules.length <= 1 ? KEEP_ONE : undefined}
                onClick={() => {
                  const id = editing.draft.id;
                  setEditing(null);
                  // A draft was never saved — removing it just discards it.
                  if (!editing.isNew) removeRule(editTrigger, id);
                }}
              >
                Remove
              </button>
              <button className="btn primary" onClick={saveSheet}>
                Save
              </button>
            </>
          }
        >
          <div className="f-field" style={{ marginBottom: 14 }}>
            <span className="f-label">Create this task</span>
            <button
              type="button"
              className={`toggle${editing.draft.enabled ? ' on' : ''}`}
              role="switch"
              aria-checked={editing.draft.enabled}
              aria-label="Create this task"
              onClick={() =>
                setEditing({ ...editing, draft: { ...editing.draft, enabled: !editing.draft.enabled } })
              }
            />
          </div>
          <div className="f-field" style={{ marginBottom: 14 }}>
            <span className="f-label">Task title</span>
            <input
              className="f-input"
              value={editing.draft.title}
              maxLength={TITLE_MAX}
              aria-label="Task title"
              aria-invalid={editing.error === TITLE_ERROR || undefined}
              onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, title: e.target.value }, error: null })}
            />
          </div>
          <div className="f-field">
            <span className="f-label">Due · days {editTrigger.anchor}</span>
            <div className="ft-stepper">
              <button type="button" className="cbtn" aria-label="Fewer days" onClick={() => stepOffset(-1)}>
                −
              </button>
              <input
                className="f-input mono"
                type="number"
                inputMode="numeric"
                min={0}
                max={OFFSET_MAX}
                value={editing.draftOffset}
                aria-label="Days"
                aria-invalid={editing.error === OFFSET_ERROR || undefined}
                onChange={(e) => setEditing({ ...editing, draftOffset: e.target.value, error: null })}
              />
              <button type="button" className="cbtn" aria-label="More days" onClick={() => stepOffset(1)}>
                +
              </button>
            </div>
          </div>
          {editing.error && <span className="error-text ft-error" role="alert">{editing.error}</span>}
        </Modal>
      )}
    </div>
  );
}

/** Desktop rule row: switch · title · "due [n] days <anchor>" knob · remove.
    Title and offset keep a local draft and commit on blur or Enter, the
    Automations hub KnobInput pattern. An invalid value stays in the input
    with an inline error and is not sent; unchanged values just settle. */
function RuleRow({
  rule,
  trigger,
  switchEl,
  error,
  onlyOne,
  onSave,
  onRemove,
}: {
  rule: Rule;
  trigger: Trigger;
  switchEl: ReactNode;
  error: FieldError | null;
  onlyOne: boolean;
  onSave: (patch: Partial<Rule>) => void;
  onRemove: () => void;
}) {
  const [title, setTitle] = useState(rule.title);
  const [offset, setOffset] = useState(String(rule.offset_days));
  const [titleError, setTitleError] = useState(false);
  const [offsetError, setOffsetError] = useState(false);

  useEffect(() => {
    setTitle(rule.title);
    setTitleError(false);
  }, [rule.title]);
  useEffect(() => {
    setOffset(String(rule.offset_days));
    setOffsetError(false);
  }, [rule.offset_days]);

  const commitTitle = () => {
    const next = title.trim();
    if (!validTitle(next)) {
      setTitleError(true);
      return;
    }
    setTitleError(false);
    setTitle(next);
    if (next !== rule.title) onSave({ title: next });
  };

  const commitOffset = () => {
    const n = parseOffset(offset);
    if (n === null) {
      setOffsetError(true);
      return;
    }
    setOffsetError(false);
    setOffset(String(n));
    if (n !== rule.offset_days) onSave({ offset_days: n });
  };

  const blurOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const shownOffset = Number(offset);

  return (
    <>
      <div className={`ft-rule${rule.enabled ? '' : ' off'}`}>
        {switchEl}
        <input
          className="f-input ft-title"
          value={title}
          maxLength={TITLE_MAX}
          aria-label="Task title"
          aria-invalid={titleError || error?.field === 'title' || undefined}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={blurOnEnter}
        />
        <div className="atm-knob">
          <div className="kv">
            <span className="ku">due</span>
            <input
              className="f-input small"
              type="number"
              min={0}
              max={OFFSET_MAX}
              value={offset}
              aria-label={`Days ${trigger.anchor}`}
              aria-invalid={offsetError || error?.field === 'offset_days' || undefined}
              onChange={(e) => setOffset(e.target.value)}
              onBlur={commitOffset}
              onKeyDown={blurOnEnter}
            />
            <span className="ku">
              day{shownOffset === 1 ? '' : 's'} {trigger.anchor}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="ft-remove"
          aria-label={onlyOne ? KEEP_ONE : `Remove ${rule.title}`}
          title={onlyOne ? KEEP_ONE : 'Remove task'}
          disabled={onlyOne}
          onClick={onRemove}
        >
          ×
        </button>
      </div>
      {titleError && <span className="error-text ft-error" role="alert">{TITLE_ERROR}</span>}
      {offsetError && <span className="error-text ft-error" role="alert">{OFFSET_ERROR}</span>}
      {error && !titleError && !offsetError && (
        <span className="error-text ft-error" role="alert">{error.message}</span>
      )}
    </>
  );
}
